/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { AxiosResponse } from "axios";

import { CONSULT_STATUS } from "@/constants/care";
import {
  ConsultComputations,
  ConsultsContext,
} from "@/contexts/consultsContext";
import { careService } from "@/services/care";
import { Consult } from "@/types/care";
import {
  categorizeTreatments,
  findPrescriptionBySlug,
  TreatmentCategories,
} from "@/utils/treatmentUtils";

export const ConsultsContextProvider = ({
  isAuthenticated,
  children,
}: {
  isAuthenticated: boolean;
  children: ReactNode;
}) => {
  const [error, setError] = useState("");
  const [consults, setConsults] = useState<Consult[]>([]);
  const [loading, setLoading] = useState(true);
  const [refetch, setRefetch] = useState(0);

  useEffect(() => {
    async function fetchConsults() {
      setLoading(true);
      careService.fetchConsults(
        (response: AxiosResponse<Consult[]>) => {
          setConsults(response.data);
          setError("");
          setLoading(false);
          return response;
        },
        (error: any, response: any) => {
          console.error("DEBUG - API Error:", error, response);
          setConsults([]);
          setError(error.message);
          setLoading(false);
        }
      );
    }

    if (isAuthenticated) {
      fetchConsults();
    }
  }, [isAuthenticated, refetch]);

  const refetchConsults = useCallback(() => {
    if (!loading) {
      setRefetch((prev) => prev + 1);
    }
  }, [loading]);

  // Compute derived consult data
  const consultComputations = useMemo((): ConsultComputations => {
    if (!consults || consults.length === 0) {
      return {
        openConsults: [],
        completedConsults: [],
        vaginitisOrUngatedRxConsults: [],
        stiConsults: [],
        malePartnerConsults: [],
        openUngatedRxConsults: [],
        mostRecentVaginitisOrUngatedRxConsult: undefined,
        firstSTIConsult: undefined,
        mostRecentCompletedVaginitisOrUngatedRxConsult: undefined,
        firstConsultWithRefills: undefined,
        hasPastConsultMessages: false,
        hasPastTreatments: false,
        latestConsult: undefined,
        latestPaidConsult: undefined,
        latestPreSubmitConsult: undefined,
        latestConsultInProgress: undefined,
      };
    }

    // Status-based computations
    const openConsults = consults.filter(
      (consult) => !consult.is_closed && consult.consult_paid
    );
    const completedConsults = consults.filter(
      (consult) => consult.status === CONSULT_STATUS.COMPLETE
    );

    // Type-based computations
    const vaginitisOrUngatedRxConsults = consults.filter(
      (consult) => consult.type === "vaginitis" || consult.type === "ungated-rx"
    );
    const stiConsults = consults.filter((consult) => consult.type === "sti");
    const malePartnerConsults = consults.filter(
      (consult) => consult.type === "male-partner"
    );
    const openUngatedRxConsults = consults.filter(
      (consult) => consult.type === "ungated-rx" && !consult.is_closed
    );

    // Most recent/specific consults
    const mostRecentVaginitisOrUngatedRxConsult =
      vaginitisOrUngatedRxConsults[0];
    const firstSTIConsult = stiConsults[0];
    const mostRecentCompletedVaginitisOrUngatedRxConsult =
      completedConsults.find(
        (consult) =>
          consult.type === "vaginitis" || consult.type === "ungated-rx"
      );
    const firstConsultWithRefills = completedConsults.find(
      (consult) => consult.ready_for_refills
    );

    // Past treatments/messages computations
    const hasPastConsultMessages = completedConsults.some(
      (c) => c?.consultmessagethread
    );

    // Use latest consult to determine past treatments logic
    const latestConsult = consults[0];
    const hasPastTreatments =
      latestConsult?.status === CONSULT_STATUS.COMPLETE
        ? completedConsults.length > 1
        : completedConsults.length > 0;

    const latestPaidConsult = consults.find((consult) => consult.consult_paid);
    const latestPreSubmitConsult = consults.find(
      (consult) => consult.is_pre_submit
    );
    const latestConsultInProgress = consults.find(
      (consult) => consult.show_consult_in_progress
    );

    return {
      openConsults,
      completedConsults,
      vaginitisOrUngatedRxConsults,
      stiConsults,
      malePartnerConsults,
      openUngatedRxConsults,
      mostRecentVaginitisOrUngatedRxConsult,
      firstSTIConsult,
      mostRecentCompletedVaginitisOrUngatedRxConsult,
      firstConsultWithRefills,
      hasPastConsultMessages,
      hasPastTreatments,
      latestConsult,
      latestPaidConsult,
      latestPreSubmitConsult,
      latestConsultInProgress,
    };
  }, [consults]);

  // Treatment processing methods
  const getTreatmentsForConsult = useCallback(
    (consultId: string): TreatmentCategories => {
      const consult = consults.find((c) => c.uid === consultId);
      if (!consult || !consult.prescriptions) {
        return {
          allPrescriptions: [],
          prescriptionsWithNoRefillsRemaining: [],
          prescriptionsWithRefillsRemaining: [],
          prescriptionsNotRefillable: [],
          alwaysRefillableTreatments: [],
        };
      }
      return categorizeTreatments(consult.prescriptions);
    },
    [consults]
  );

  const findPrescriptionBySlugForConsult = useCallback(
    (consultId: string, slug: string) => {
      const consult = consults.find((c) => c.uid === consultId);
      if (!consult || !consult.prescriptions) {
        return undefined;
      }
      return findPrescriptionBySlug(consult.prescriptions, slug);
    },
    [consults]
  );

  return (
    <ConsultsContext.Provider
      value={{
        consults,
        error,
        setConsults,
        loading,
        refetchConsults,
        consultComputations,
        getTreatmentsForConsult,
        findPrescriptionBySlug: findPrescriptionBySlugForConsult,
      }}
    >
      {children}
    </ConsultsContext.Provider>
  );
};
